import dayjs from 'dayjs'
import React from 'react'

import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { useAppearance } from '@fourthwall/appearance'
import { PriceString } from '@fourthwall/components'
import type { AnalyticsChartProps } from '@fourthwall/module-analytics'
import { Stat, mapChartToSeriesData, useAnalyticsChartQuery } from '@fourthwall/module-analytics'
import { getPrice } from '@fourthwall/utils/dist/common'
import { useCurrentShop } from '@modules/Dashboard/hooks/useCurrentShop'
import { useShopTime } from '@modules/Timezone/providers/ShopTimeProvider'
import { reverse, routing } from '@utils/routing'

import { getDateFormat, getFrom, getPrecision, getRangeQueryParam, getTo } from '../../utils'

type Props = {
  label: string
  range: number
}

export const ProfitChart = ({ label, range }: Props) => {
  const { timezone, getCurrentShopTime, getCurrentRelativeShopTime } = useShopTime()
  const from = getFrom(getCurrentShopTime(), range)
  const to = getTo(getCurrentShopTime())
  const { appearance } = useAppearance()
  const { hasPermission } = useDashboardPermissions()
  const { currentShop } = useCurrentShop({ staleTime: Infinity })

  const { analyticsChartQuery: profitQuery } = useAnalyticsChartQuery([
    {
      from,
      to,
      precision: getPrecision(range),
      chartTypes: ['PROFIT'],
      chartEntityGroup: ['SUBSCRIPTIONS'],
      sid: [currentShop?.id!],
      zoneId: timezone,
    },
  ])

  const series: AnalyticsChartProps['series'] = [
    {
      label: 'Profit',
      data: mapChartToSeriesData(
        profitQuery.data?.current.profit?.data,
        {
          from,
          to,
          precision: getPrecision(range),
        },
        {
          allowPoints: false,
          now: getCurrentRelativeShopTime(),
        },
      ),
    },
  ]

  const chartTo = hasPermission('analytics.profit')
    ? `${reverse(routing.analytics.overview.report.self, {
        reportType: 'profit',
      })}?chartEntity=SUBSCRIPTIONS&range=${getRangeQueryParam(range)}`
    : undefined

  return (
    <Stat
      appearance={appearance}
      label={label}
      to={chartTo || ''}
      loading={!profitQuery.data || profitQuery.isFetching}
      value={<PriceString value={profitQuery.data?.current.profit?.aggregatedValue || 0} />}
      chart={{
        formatDate: (date) => dayjs(date).format(getDateFormat(getPrecision(range))),
        formatValue: (value) => getPrice(value),
        series,
      }}
    />
  )
}
